import React, { FC, MutableRefObject } from 'react';

import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import ArrowUpIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownIcon from '@mui/icons-material/ArrowDownwardOutlined';
import classnames from 'classnames';
import { useHeight } from 'framework/utils';
import useCollapsibleHelper, { CollapsibleProps, DEFAULT_MAX_HEIGHT } from './collapsibleHelper';
import { useUiTheme } from 'framework/uiTheme';
import { Button } from '../../mui-builder/Button/Button';
import Alert from '../../mui-builder/Alert/Alert';
import Loading from '../../mui-builder/Loading/Loading';
import { Box } from '@mui/material';

const Collapsible: FC<CollapsibleProps> = (props: CollapsibleProps) => {
  const { children, maxHeight = DEFAULT_MAX_HEIGHT } = props;
  const { ref, height } = useHeight();
  const {
    isCollapsible,
    isExpandableField,
    isExpanded,
    displayedValue,
    isLoading,
    toggleBlock,
    retryUploadData,
    error,
  } = useCollapsibleHelper({
    ...props,
    height,
  });
  const isBlockExpanded = isExpanded || !isCollapsible;
  const { Button: ThemedButton = Button, Alert: ThemedAlert = Alert, Loading: ThemedLoading = Loading } = useUiTheme();

  const renderText = () => {
    if (isLoading) {
      return <ThemedLoading />;
    }

    if (error) {
      return (
        <ThemedAlert severity="error" sx={{ mb: 0 }}>
          <div>{error}</div>
          <ThemedButton variant="text" onClick={retryUploadData} startIcon={<RefreshIcon />}>
            Try again
          </ThemedButton>
        </ThemedAlert>
      );
    }

    return (
      <div ref={ref as MutableRefObject<Nullable<HTMLDivElement>>}>
        {React.cloneElement(children, {
          value: displayedValue,
        })}
      </div>
    );
  };

  return (
    <>
      <Box
        className={classnames('collapsible', { 'show-blur': !isBlockExpanded })}
        sx={{
          overflow: 'hidden',
          transition: 'max-height 0.2s',
          position: 'relative',
          maxHeight: isBlockExpanded || isExpandableField ? height : maxHeight,
          height: isLoading ? maxHeight : 'inherit',
          '&::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            width: '100%',
            left: 0,
            bottom: 0,
            height: '50px',
            opacity: isBlockExpanded ? 0 : 0.9,
            backgroundImage: theme =>
              `linear-gradient(0, ${theme.palette.common.white}, ${theme.palette.common.transparentWhite})`,
            transition: 'opacity 0.3s',
            zIndex: isBlockExpanded ? -1 : 'inherit',
          },
        }}
      >
        {renderText()}
      </Box>
      {isCollapsible && (
        <ThemedButton
          type="button"
          color="primary"
          size="small"
          onClick={toggleBlock}
          disabled={isLoading}
          endIcon={isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
          sx={{
            display: 'flex',
            width: '100%',
            marginTop: isBlockExpanded && !isLoading ? '16px' : '8px',
            marginBottom: '16px',
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </ThemedButton>
      )}
    </>
  );
};

export default Collapsible;
